import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Download, AlertTriangle, TrendingDown, DollarSign, Users, Mail, Phone, ChevronRight, X, Calendar, FileText } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Skeleton, Modal } from "@/components/ui-kit";
import { formatIDR } from "@/lib/format";

/**
 * Iter v34b1 — Vendor Reconciliation Dashboard.
 * AP Aging: outstanding hutang usaha per vendor, buckets current / 0-30 / 31-60 / 61-90 / 90+ hari.
 * Backend: GET /vendor-reconciliation/summary, GET /vendor-reconciliation/{id}/details
 */
const BUCKET_LABELS = {
  "current": "Belum Jatuh Tempo",
  "0-30": "0-30 Hari Overdue",
  "31-60": "31-60 Hari Overdue",
  "61-90": "61-90 Hari Overdue",
  "90+": "90+ Hari Overdue",
};
const BUCKET_COLORS = {
  "current": "bg-stone-100 text-stone-700 border-stone-300",
  "0-30": "bg-amber-50 text-amber-800 border-amber-300",
  "31-60": "bg-orange-50 text-orange-800 border-orange-300",
  "61-90": "bg-red-50 text-red-800 border-red-300",
  "90+": "bg-red-100 text-red-900 border-red-400",
};
const BUCKET_ORDER = ["current", "0-30", "31-60", "61-90", "90+"];

export default function VendorReconciliation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [bucketFilter, setBucketFilter] = useState("");
  const [detailVendor, setDetailVendor] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/vendor-reconciliation/summary");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data rekonsiliasi vendor");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openDetails = async (vendor) => {
    setDetailVendor(vendor);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const key = vendor.vendor_id || `__name__:${vendor.vendor_name}`;
      const r = await api.get(`/vendor-reconciliation/${encodeURIComponent(key)}/details`);
      setDetailData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat detail");
    } finally { setDetailLoading(false); }
  };

  const filteredVendors = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    return (data.vendors || []).filter((v) => {
      if (bucketFilter && (v.buckets[bucketFilter] || 0) <= 0) return false;
      if (s) {
        const hay = `${v.vendor_name} ${v.vendor_email} ${v.vendor_phone}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [data, search, bucketFilter]);

  const exportCSV = () => {
    if (!filteredVendors.length) { toast.error("Tidak ada data untuk diekspor"); return; }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Vendor", "Email", "Telepon", "Jumlah Expense", ...BUCKET_ORDER.map((b) => BUCKET_LABELS[b] + " (Rp)"), "Total Outstanding (Rp)"].map(esc).join(",");
    const rows = filteredVendors.map((v) => [
      v.vendor_name, v.vendor_email, v.vendor_phone, v.expense_count,
      ...BUCKET_ORDER.map((b) => v.buckets[b] || 0),
      v.total_outstanding,
    ].map(esc).join(","));
    const totalRow = ["TOTAL", "", "", data.open_expense_count, ...BUCKET_ORDER.map((b) => data.buckets[b] || 0), data.total_outstanding].map(esc).join(",");
    const csv = [header, ...rows, totalRow].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Vendor_Reconciliation_${data.as_of_date}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-full bg-paper p-4 md:p-6 lg:p-8">
      <div className="max-w-[1500px] mx-auto space-y-6">
        <PageHeader
          overline="Keuangan · Vendor"
          title="Rekonsiliasi Vendor (AP Aging)"
          description="Hutang usaha kepada vendor per bucket aging (0-30, 31-60, 61-90, 90+ hari overdue)."
          action={
            <div className="flex items-center gap-2">
              <Button onClick={load} variant="secondary" disabled={loading} data-testid="vr-refresh"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}/> Reload</Button>
              <Button onClick={exportCSV} disabled={!filteredVendors.length} data-testid="vr-export-csv"><Download className="h-3.5 w-3.5"/> Export CSV</Button>
            </div>
          }
        />

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard icon={DollarSign} label="Total Outstanding" value={formatIDR(data?.total_outstanding || 0)} loading={loading} tone="blue" testid="vr-total-outstanding"/>
          <SummaryCard icon={Users} label="Total Vendor" value={data?.vendor_count ?? 0} loading={loading} testid="vr-vendor-count"/>
          <SummaryCard icon={FileText} label="Expense Terbuka" value={data?.open_expense_count ?? 0} loading={loading} testid="vr-expense-count"/>
          <SummaryCard icon={AlertTriangle} label="Overdue > 60 Hari" value={formatIDR((data?.buckets?.["61-90"] || 0) + (data?.buckets?.["90+"] || 0))} loading={loading} tone="red" testid="vr-critical"/>
        </div>

        {/* Bucket breakdown */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-stone-800 mb-3">Bucket Aging</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {BUCKET_ORDER.map((b) => (
              <button
                key={b}
                onClick={() => setBucketFilter(bucketFilter === b ? "" : b)}
                className={`p-3 rounded-xl border text-left transition ${
                  bucketFilter === b ? "ring-2 ring-blue-500 ring-offset-1 " : "hover:shadow-md "
                }${BUCKET_COLORS[b]}`}
                data-testid={`vr-bucket-${b}`}
              >
                <div className="text-[10px] uppercase tracking-widest font-semibold">{BUCKET_LABELS[b]}</div>
                {loading ? <Skeleton className="h-6 w-20 mt-2"/> : (
                  <div className="font-display text-lg mt-1 font-mono-num">{formatIDR(data?.buckets?.[b] || 0)}</div>
                )}
              </button>
            ))}
          </div>
          {bucketFilter && (
            <div className="mt-3 text-xs text-blue-700 flex items-center gap-2">
              <span>Filter aktif: <b>{BUCKET_LABELS[bucketFilter]}</b></span>
              <button onClick={() => setBucketFilter("")} className="text-red-600 hover:underline flex items-center gap-1" data-testid="vr-clear-bucket-filter"><X className="h-3 w-3"/> Bersihkan</button>
            </div>
          )}
        </Card>

        {/* Filter */}
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama vendor / email / telepon..." data-testid="vr-search"/>
            <div className="text-xs text-stone-500 self-center md:col-span-2">
              Per <b>{data?.as_of_date || "-"}</b>. Menampilkan <b>{filteredVendors.length}</b> vendor.
            </div>
          </div>
        </Card>

        {/* Vendor table */}
        <Card className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full"/>)}
            </div>
          ) : filteredVendors.length === 0 ? (
            <div className="text-center py-16 text-stone-400" data-testid="vr-empty">
              Tidak ada vendor dengan outstanding di filter ini.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="vr-table">
                <thead className="bg-stone-50">
                  <tr className="text-left text-[11px] uppercase tracking-widest text-stone-500 border-b border-stone-200">
                    <th className="py-3 px-3">#</th>
                    <th className="py-3 px-3">Vendor</th>
                    <th className="py-3 px-3 text-right">Belum J.T.</th>
                    <th className="py-3 px-3 text-right">0-30</th>
                    <th className="py-3 px-3 text-right">31-60</th>
                    <th className="py-3 px-3 text-right">61-90</th>
                    <th className="py-3 px-3 text-right">90+</th>
                    <th className="py-3 px-3 text-right">Total Outstanding</th>
                    <th className="py-3 px-3 text-center">Expense</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVendors.map((v, i) => (
                    <tr
                      key={v.vendor_id || v.vendor_name}
                      className="border-b border-stone-100 hover:bg-stone-50/50 cursor-pointer"
                      onClick={() => openDetails(v)}
                      data-testid={`vr-row-${v.vendor_id || v.vendor_name}`}
                    >
                      <td className="py-3 px-3 text-stone-500">{i + 1}</td>
                      <td className="py-3 px-3">
                        <div className="font-medium text-stone-900">{v.vendor_name}</div>
                        {v.vendor_email && <div className="text-[11px] text-stone-500 flex items-center gap-1 mt-0.5"><Mail className="h-2.5 w-2.5"/>{v.vendor_email}</div>}
                      </td>
                      {BUCKET_ORDER.map((b) => {
                        const val = v.buckets[b] || 0;
                        return (
                          <td key={b} className={`py-3 px-3 text-right font-mono-num text-xs ${val > 0 && (b === "61-90" || b === "90+") ? "text-red-700 font-semibold" : val > 0 ? "text-stone-800" : "text-stone-300"}`}>
                            {val > 0 ? formatIDR(val) : "—"}
                          </td>
                        );
                      })}
                      <td className="py-3 px-3 text-right font-mono-num font-semibold text-blue-800">{formatIDR(v.total_outstanding)}</td>
                      <td className="py-3 px-3 text-center">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-800 text-[11px] font-semibold">{v.expense_count}</span>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <ChevronRight className="w-4 h-4 text-stone-400"/>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {data && (
                  <tfoot className="bg-blue-50/60 font-semibold text-blue-900">
                    <tr>
                      <td colSpan={2} className="py-3 px-3 uppercase tracking-widest text-xs">Total</td>
                      {BUCKET_ORDER.map((b) => (
                        <td key={b} className="py-3 px-3 text-right font-mono-num text-xs">{formatIDR(data.buckets[b] || 0)}</td>
                      ))}
                      <td className="py-3 px-3 text-right font-mono-num" data-testid="vr-grand-total">{formatIDR(data.total_outstanding)}</td>
                      <td className="py-3 px-3 text-center">{data.open_expense_count}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* Detail Modal */}
      <Modal open={!!detailVendor} onClose={() => { setDetailVendor(null); setDetailData(null); }} size="lg" title={detailVendor?.vendor_name || ""}>
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4 border-b border-stone-100 pb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-1">Detail Hutang Vendor</div>
              {(detailVendor?.vendor_email || detailVendor?.vendor_phone) && (
                <div className="flex items-center gap-3 text-xs text-stone-600 mt-2">
                  {detailVendor.vendor_email && <span className="flex items-center gap-1"><Mail className="h-3 w-3"/>{detailVendor.vendor_email}</span>}
                  {detailVendor.vendor_phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3"/>{detailVendor.vendor_phone}</span>}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-stone-500">Total Outstanding</div>
              <div className="font-display text-2xl text-blue-800 font-mono-num" data-testid="vr-detail-total">{formatIDR(detailVendor?.total_outstanding || 0)}</div>
            </div>
          </div>

          {detailLoading ? (
            <div className="space-y-2 pt-4">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 w-full"/>)}
            </div>
          ) : detailData?.rows?.length ? (
            <div className="overflow-x-auto border border-stone-200 rounded-2xl">
              <table className="w-full text-sm" data-testid="vr-detail-table">
                <thead className="bg-stone-50">
                  <tr className="text-left text-[10px] uppercase tracking-widest text-stone-500 border-b border-stone-200">
                    <th className="py-2 px-3">Tanggal</th>
                    <th className="py-2 px-3">Jatuh Tempo</th>
                    <th className="py-2 px-3">Deskripsi</th>
                    <th className="py-2 px-3 text-right">Total</th>
                    <th className="py-2 px-3 text-right">Dibayar</th>
                    <th className="py-2 px-3 text-right">Outstanding</th>
                    <th className="py-2 px-3 text-center">Aging</th>
                  </tr>
                </thead>
                <tbody>
                  {detailData.rows.map((r) => (
                    <tr key={r.id} className="border-b border-stone-100" data-testid={`vr-detail-row-${r.id}`}>
                      <td className="py-2 px-3 font-mono-num text-xs">{r.date}</td>
                      <td className="py-2 px-3 font-mono-num text-xs">{r.due_date || "—"}</td>
                      <td className="py-2 px-3">
                        <div className="text-stone-800">{(r.items || []).map((it) => it.description).join(", ") || "-"}</div>
                        {r.notes && <div className="text-[10px] text-stone-500 mt-0.5">{r.notes}</div>}
                      </td>
                      <td className="py-2 px-3 text-right font-mono-num">{formatIDR(r.computed_total_bill)}</td>
                      <td className="py-2 px-3 text-right font-mono-num text-emerald-700">{formatIDR(r.computed_paid)}</td>
                      <td className="py-2 px-3 text-right font-mono-num font-semibold text-blue-800">{formatIDR(r.outstanding)}</td>
                      <td className="py-2 px-3 text-center">
                        <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border font-semibold ${BUCKET_COLORS[r.bucket]}`}>{r.bucket}</span>
                        {r.days_overdue !== null && r.days_overdue >= 0 && (
                          <div className="text-[9px] text-stone-500 mt-0.5">{r.days_overdue} hari</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-stone-400 border border-stone-200 rounded-2xl">
              Tidak ada expense outstanding untuk vendor ini.
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, loading, tone, testid }) {
  const toneClass = tone === "blue" ? "bg-gradient-to-br from-blue-50 to-white border-blue-100"
    : tone === "red" ? "bg-gradient-to-br from-red-50 to-white border-red-100"
    : "bg-white border-stone-200/70";
  const iconColor = tone === "blue" ? "text-blue-600" : tone === "red" ? "text-red-600" : "text-stone-400";
  const valueColor = tone === "blue" ? "text-blue-800" : tone === "red" ? "text-red-800" : "text-stone-900";
  return (
    <div className={`rounded-2xl border p-4 ${toneClass}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
        <Icon className={`h-3.5 w-3.5 ${iconColor}`}/> {label}
      </div>
      {loading ? <Skeleton className="h-8 w-24 mt-2"/> : (
        <div className={`font-display text-xl mt-2 font-mono-num ${valueColor}`}>{value}</div>
      )}
    </div>
  );
}
